import { useState, useCallback, useRef } from 'react';
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import { Button, DateDisplay } from '@aidkitorg/component-library';
import { useDownloader } from '../API';

/** The export fields any downloader (admin or share-link holder) is shown. */
export type ProgramImagesExportSummary = NonNullable<extractReturnAsJson<Directory['/images_export/status']>['programExport']>;

export type GetPartUrl = (part: number) => Promise<{ url: string, name: string } | { error: unknown } | undefined>;

/** Local date and time to the minute, e.g. "October 7, 2026, 2:15 PM EDT". */
export function ExportDate({ value, className = 'text-base' }: { value: number, className?: string }) {
  return <DateDisplay
    className={className}
    utcValue={value}
    displayInUTC={false}
    options={{ year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', timeZoneName: 'short' }}
  />;
}

export function ProgramImagesExportProgress({ programExport }: { programExport: ProgramImagesExportSummary }) {
  const { rowsDone, rowsTotal, filesWritten, filesLost, parts } = programExport;
  const pct = rowsTotal > 0 ? Math.min(100, Math.floor(rowsDone / rowsTotal * 100)) : 0;
  return <p>
    {rowsTotal > 0 ? `${pct}% — ` : ''}{rowsDone.toLocaleString()} of {rowsTotal.toLocaleString()} records processed,{' '}
    {filesWritten.toLocaleString()} files in {parts.length} zip file{parts.length === 1 ? '' : 's'} so far
    {filesLost > 0 && <>, <span className="text-red-700">{filesLost.toLocaleString()} missing</span></>}.
  </p>;
}

// Documents whose stored link pointed at nothing in storage, even after retries
// and a prefix search. They can't be recovered by re-running the export.
export function ProgramImagesExportLostFiles({ programExport }: { programExport: ProgramImagesExportSummary }) {
  const { filesLost, lostUrls } = programExport;
  if (filesLost === 0) return null;
  const truncated = lostUrls.length < filesLost;
  return <details className="border border-red-200 bg-red-50 rounded p-3">
    <summary className="cursor-pointer text-red-800">
      {filesLost.toLocaleString()} document{filesLost === 1 ? ' was' : 's were'} referenced by an application but could not be found in storage and {filesLost === 1 ? 'is' : 'are'} not included.
    </summary>
    <p className="mt-2 text-sm">
      These links no longer resolve to a file. Share this list with the partner so they know which uploads are absent.
      {truncated && ` Only the first ${lostUrls.length.toLocaleString()} are shown; the full list is in the export job logs.`}
    </p>
    <ul className="mt-2 max-h-64 overflow-auto font-mono text-xs break-all space-y-1">
      {lostUrls.map(url => <li key={url}>{url}</li>)}
    </ul>
  </details>;
}

// A presigned S3 URL only answers a cross-origin fetch if the bucket has CORS
// configured; a TypeError here means it doesn't.
async function canFetchCrossOrigin(url: string): Promise<boolean> {
  try {
    const res = await fetch(url);
    await res.body?.cancel();
    return res.ok;
  } catch {
    return false;
  }
}

/** "Download all" plus the per-part list. `getPartUrl` is whichever endpoint the
 *  caller is entitled to use; `children` renders alongside the download-all button. */
export function ProgramImagesExportDownload({ programExport, getPartUrl, children }: {
  programExport: ProgramImagesExportSummary,
  getPartUrl: GetPartUrl,
  children?: React.ReactNode,
}) {
  const download = useDownloader();
  const [downloading, setDownloading] = useState<{ mode: 'preparing' | 'stream' | 'links', done: number, total: number, error?: string } | null>(null);
  const inFlight = useRef(false);
  const { parts } = programExport;

  const downloadOne = useCallback(async (part: number) => {
    const response = await getPartUrl(part);
    if (response && !('error' in response)) download(response.url, response.name);
  }, [getPartUrl, download]);

  // Preferred path streams each part into a folder the user picks, so a very large
  // export never sits in memory or needs N save dialogs. The picker must open
  // synchronously on the click (it needs transient activation), and it only works
  // if the exports bucket allows CORS, so probe afterwards and fall back to one
  // browser download per part if either is unavailable.
  const downloadAll = useCallback(async () => {
    if (parts.length === 0 || inFlight.current) return;
    inFlight.current = true;
    const total = parts.length;
    setDownloading({ mode: 'preparing', done: 0, total });

    try {
      let dir: FileSystemDirectoryHandle | undefined;
      if (typeof window.showDirectoryPicker === 'function') {
        try {
          dir = await window.showDirectoryPicker({ id: 'programImagesExport', mode: 'readwrite', startIn: 'downloads' });
        } catch (e: any) {
          if (e?.name === 'AbortError') {
            setDownloading(null);
            return;
          }
        }
      }

      const first = await getPartUrl(parts[0].part);
      if (!first || 'error' in first) throw new Error('Could not get a link for part 1');
      if (dir && !(await canFetchCrossOrigin(first.url))) dir = undefined;

      const mode = dir ? 'stream' : 'links';
      setDownloading({ mode, done: 0, total });
      for (let i = 0; i < total; i++) {
        if (i > 0 && !dir) await new Promise(r => setTimeout(r, 1500));
        const response = i === 0 ? first : await getPartUrl(parts[i].part);
        if (!response || 'error' in response) throw new Error(`Could not get a link for part ${i + 1}`);
        if (dir) {
          const res = await fetch(response.url);
          if (!res.ok || !res.body) throw new Error(`Download of ${response.name} failed (${res.status})`);
          const handle = await dir.getFileHandle(response.name, { create: true });
          await res.body.pipeTo(await handle.createWritable());
        } else {
          download(response.url, response.name);
        }
        setDownloading({ mode, done: i + 1, total });
      }
    } catch (e: any) {
      setDownloading(d => ({ mode: d?.mode ?? 'links', done: d?.done ?? 0, total, error: e?.message ?? String(e) }));
    } finally {
      inFlight.current = false;
    }
  }, [parts, getPartUrl, download]);

  const downloadingAll = !!downloading && !downloading.error && (downloading.mode === 'preparing' || downloading.done < downloading.total);

  return <div className="space-y-4">
    <div className="flex gap-2 items-center">
      {parts.length > 1 && <Button onClick={downloadAll} loading={downloadingAll} isDisabled={downloadingAll}>
        Download all {parts.length} zip files
      </Button>}
      {children}
    </div>
    {downloading && <p className={downloading.error ? 'text-red-700' : ''}>
      {downloading.error
        ? `Stopped after ${downloading.done} of ${downloading.total}: ${downloading.error}`
        : downloading.mode === 'preparing'
          ? 'Preparing…'
          : downloading.done < downloading.total
            ? `${downloading.mode === 'stream' ? 'Downloading' : 'Starting download'} ${downloading.done + 1} of ${downloading.total}…`
            : downloading.mode === 'stream'
              ? `All ${downloading.total} files downloaded.`
              : `Started ${downloading.total} downloads. If your browser asked whether to allow multiple downloads and you declined, use the links below for any that are missing.`}
    </p>}
    <ul className="divide-y border rounded">
      {parts.map(p => <li key={p.part} className="flex justify-between items-center px-3 py-2">
        <span className="font-mono text-sm">{p.name}</span>
        <Button variant="link" onClick={() => downloadOne(p.part)}>Download</Button>
      </li>)}
    </ul>
  </div>;
}
